import { AfterViewInit, Component, ElementRef, forwardRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { getDaysInMonth, isValid, parse } from 'date-fns';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { formatDate } from '@app/shared/date-format.pipe';
import { dateValidator } from '@app/shared/date-validator.directive';

@Component({
  selector: 'om-date-input',
  templateUrl: './date-input.component.html',
  styleUrls: ['../form-input.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateInputComponent),
      multi: true,
    },

    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DateInputComponent),
      multi: true,
    },
  ],
})
export class DateInputComponent implements OnInit, ControlValueAccessor, Validator, OnDestroy, AfterViewInit {
  private destroy$ = new Subject<void>();

  dateForm: UntypedFormGroup;
  monthMaxLength = 2;
  dayMaxLength = 2;
  // yearMaxLength is not defined since yearInput doesn't call #shiftInputFocusOnLimit (see #onYearChanged)

  @Input() required: Boolean = true;
  @Input() readOnly: Boolean = false;
  @Input() shouldDisableDob: Boolean = false;
  @ViewChild('monthInput', { static: true }) monthInput: ElementRef;
  @ViewChild('dayInput', { static: true }) dayInput: ElementRef;
  @ViewChild('yearInput', { static: true }) yearInput: ElementRef;

  constructor(public formBuilder: UntypedFormBuilder) {}

  ngOnInit() {
    this.createForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit() {}

  createForm() {
    this.dateForm = this.formBuilder.group({
      date: [undefined, dateValidator],
      month: [
        undefined,
        [
          Validators.required,
          Validators.min(1),
          Validators.max(12),
          Validators.maxLength(2),
          Validators.pattern('^[0-9]*$'),
        ],
      ],
      day: [
        undefined,
        [
          Validators.required,
          Validators.min(1),
          Validators.max(31),
          Validators.maxLength(2),
          Validators.pattern('^[0-9]*$'),
          this.validateDayOfMonth.bind(this),
        ],
      ],
      year: this.formBuilder.control(undefined, {
        validators: [
          Validators.required,
          Validators.min(1900),
          Validators.minLength(4),
          Validators.maxLength(4),
          Validators.pattern('^[0-9]*$'),
        ],
        updateOn: 'change',
      }),
    });

    if (this.shouldDisableDob) {
      this.dateForm.get('month').disable();
      this.dateForm.get('day').disable();
      this.dateForm.get('year').disable();
    }

    this.dateForm.controls.month.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => this.onMonthChanged(data));
    this.dateForm.controls.day.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(data => this.onDayChanged(data));
    this.dateForm.controls.year.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(_data => this.onYearChanged());
  }

  buildDate(formValue: { month: string; day: string; year: string }) {
    const { month, day, year } = formValue;
    let date;
    if ([month, day, year].join('') === '' && !this.required) {
      date = undefined;
      this.markAsPristineAndUntouched();
    } else {
      const value =
        month === '' || year === '' || day === '' ? undefined : new Date(Number(year), Number(month) - 1, Number(day));
      date = isValid(value) ? formatDate(value, 'yyyy-MM-dd') : undefined;
      this.dateForm.controls.date.markAsDirty();
    }
    this.dateForm.controls.date.setValue(date, { emitEvent: false });
  }

  markAsTouchedAndDirty(): void {
    ['day', 'month', 'year', 'date'].forEach(input => {
      this.dateForm.controls[input].markAsTouched();
      this.dateForm.controls[input].markAsDirty();
    });
  }

  markAsPristineAndUntouched(): void {
    ['day', 'month', 'year', 'date'].forEach(input => {
      this.dateForm.controls[input].markAsPristine();
      this.dateForm.controls[input].markAsUntouched();
    });
  }

  onTouched: () => void = () => {};

  writeValue(val: any): void {
    if (val && val.date) {
      const date = new Date(val.date);
      const formState = {
        date: val.date,
        month: date.getMonth() + 1,
        day: date.getDate(),
        year: date.getFullYear(),
      };

      this.dateForm.setValue(formState, { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this.dateForm.valueChanges.subscribe(data => {
      this.buildDate(data);
      fn(this.dateForm.value);
    });
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  validate(_c: AbstractControl): ValidationErrors | undefined {
    return this.dateForm.valid || (!this.required && this.emptyDate())
      ? undefined
      : { dateForm: { valid: false, message: 'date fields are invalid' } };
  }

  private emptyDate() {
    const { date } = this.dateForm.value;
    return date === null || date === undefined;
  }

  private onYearChanged() {
    this.dateForm.get('day').updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }

  private onMonthChanged(data: string) {
    const monthsInYearLimiter = 1;
    this.shiftInputFocusOnLimit(data, monthsInYearLimiter, this.monthMaxLength, this.dayInput);
    this.dateForm.get('day').updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }

  private onDayChanged(data: string) {
    const daysInMonthLimiter = 3;
    this.shiftInputFocusOnLimit(data, daysInMonthLimiter, this.dayMaxLength, this.yearInput);
  }

  /**
   * Checks input value to see if it is 'complete' enough to move on to the next input
   *   e.g. By limit: Month values cannot start with a '2', so if the user enters digit >= 2
   *   e.g. By length: If a user types 2 digits as a day input ('01', '29')
   *
   * @param rawValue  Raw value from the input
   * @param limit     A value to check against 'data' to see if it's safe to focus the next input box
   * @param maxLength The max length of the raw input
   * @param nextInput The next element
   */
  private shiftInputFocusOnLimit(rawValue: string, limit: number, maxLength: number, nextInput: ElementRef) {
    const inputValue = parseInt(rawValue, 10);
    const maxLengthMet = rawValue.length === maxLength;
    const valueLimitMet = inputValue > limit;

    if (!isNaN(inputValue) && (maxLengthMet || valueLimitMet) && nextInput) {
      nextInput.nativeElement.focus();
    }
  }

  get valid() {
    return this.dateForm.valid;
  }

  private validateDayOfMonth(control: AbstractControl): ValidationErrors | undefined {
    if (!this.dateForm) {
      return;
    }

    const year: string = this.dateForm.get('year').value;
    const month: string = this.dateForm.get('month').value;
    const day: string = control.value;
    if (!year || !month || !day) {
      return;
    }

    const date = parse(`${year}-${month}`, 'yyyy-MM', new Date());
    const dayValue = Number.parseInt(day, 10);
    const daysInMonth = getDaysInMonth(date);
    return dayValue > daysInMonth ? { max: { max: daysInMonth, actual: dayValue } } : undefined;
  }
}
